import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { stateOptions } from "data";
import { StateMileageSelect } from "./StateMileageSelect";

const StateMileageSchema = z.object({
  state: z.string().min(1, "Required"),
  percentage: z.coerce
    .number({ invalid_type_error: "Required" })
    .int("Whole number only")
    .min(1, "Min 1%")
    .max(100, "Max 100%"),
  miles: z.coerce
    .number({ invalid_type_error: "Please enter miles in this state" })
    .min(0, "Miles cannot be negative"),
});

export function StateMileageDialog({ open, onOpenChange, onAdd, defaultValues, annualMileage }) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    resolver: zodResolver(StateMileageSchema),
    mode: "all",
    defaultValues: {
      state: "",
      percentage: undefined,
      miles: undefined,
      ...defaultValues,
    },
  });

  const percentage = watch("percentage");
  const miles = watch("miles");
  const manualMilesRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      reset({
        state: defaultValues?.state || "",
        percentage: defaultValues?.percentage ?? undefined,
        miles: defaultValues?.miles ?? undefined,
      });
      manualMilesRef.current = !!defaultValues?.miles;
    }
  }, [open, defaultValues, reset]);

  React.useEffect(() => {
    const annual = Number(annualMileage);
    const pct = Number(percentage);
    if (
      !Number.isFinite(annual) ||
      annual <= 0 ||
      !Number.isFinite(pct) ||
      pct <= 0
    ) {
      return;
    }
    if (manualMilesRef.current && miles != null && miles !== "") return;
    const computed = Math.round((annual * pct) / 100);
    setValue("miles", computed, { shouldValidate: true, shouldDirty: true });
  }, [annualMileage, percentage, miles, setValue]);

  const clampPercent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (n < 1) return 1;
    if (n > 100) return 100;
    return Math.trunc(n);
  };

  const closeAndReset = () => {
    reset();
    manualMilesRef.current = false;
    onOpenChange(false);
  };

  const isEdit = !!defaultValues;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit State Mileage" : "Add State Mileage"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((values) => {
            onAdd(values);
            closeAndReset();
          })}
          className="space-y-5"
        >
          {/* State select */}
          <div className="space-y-2">
            <Label>Select your state *</Label>
            <StateMileageSelect
              control={control}
              errors={errors}
              stateOptions={stateOptions}
            />
            {errors.state && (
              <p id="state-error" className="text-xs text-red-600">
                {errors.state.message}
              </p>
            )}
          </div>

          {/* Percent input */}
          <div className="space-y-2">
            <Label>What percent of your loads are this state? *</Label>
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              min={1}
              max={100}
              placeholder="Enter percent (1-100)"
              className={
                errors.percentage
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }
              aria-invalid={!!errors.percentage}
              aria-describedby={
                errors.percentage ? "percentage-error" : undefined
              }
              {...register("percentage")}
              onBlur={(e) => {
                const clamped = clampPercent(e.target.value);
                setValue("percentage", clamped, { shouldValidate: true });
              }}
            />
            {errors.percentage && (
              <p id="percentage-error" className="text-xs text-red-600">
                {errors.percentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>How many miles of your loads are in this state?</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="Enter miles"
              className={
                errors.miles ? "border-red-500 focus-visible:ring-red-500" : ""
              }
              aria-invalid={!!errors.miles}
              aria-describedby={errors.miles ? "miles-error" : undefined}
              {...register("miles", {
                onChange: () => {
                  manualMilesRef.current = true;
                },
              })}
            />
            <p className="text-xs text-gray-500">
              Auto-filled from annual mileage and percent; you can override.
            </p>
            {errors.miles && (
              <p id="miles-error" className="text-xs text-red-600">
                {errors.miles.message}
              </p>
            )}
          </div>

          <DialogFooter className="pt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => reset()}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              disabled={!isValid || isSubmitting}
            >
              {isEdit ? "Save changes" : "Add state mileage"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
